import {CommentDiscussionIcon, RocketIcon} from '@primer/octicons-react'
import {
  checksStatusPresentation,
  conflictsStatusPresentation,
  reviewStatusPresentation,
  type CondensedStatusRow,
} from '../../helpers/condensed-merge-status-presentation'
import {useMergeboxStatusCalculator} from '../../hooks/use-mergebox-status-calculator'
import type {Channels} from '../../hooks/use-mergeability-live-updates'
import {useUnresolvedConversationCount} from '@github-ui/pull-requests/layout-selectors'
import {useValidatedMergeData} from './use-validated-merge-data'

// Stable fallback so the calculator hook isn't handed a fresh object every render.
const EMPTY_CHANNELS: Channels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
  codeScanningChannel: null,
}

export function useMergeStatusRows(): CondensedStatusRow[] | null {
  const validatedMergeData = useValidatedMergeData()

  // hack: the hooks below were not built with undefined/null in mind
  const headSha = validatedMergeData?.headSha ?? ''
  const channels: Channels = validatedMergeData?.channels ?? EMPTY_CHANNELS

  const {calculator, statusChecksData} = useMergeboxStatusCalculator({headSha, channels})
  const unresolvedConversationCount = useUnresolvedConversationCount() ?? 0

  if (!validatedMergeData) return null

  const rows: CondensedStatusRow[] = []
  const {ChecksSection, ConflictsSection, ReviewerSection, BlockedSection} = calculator.sections

  if (ChecksSection.shouldRender) {
    rows.push(checksStatusPresentation(ChecksSection, statusChecksData))
  }

  if (ConflictsSection.shouldRender) {
    rows.push(conflictsStatusPresentation(ConflictsSection))
  }

  if (ReviewerSection.shouldRender) {
    rows.push(reviewStatusPresentation(ReviewerSection))
  }

  if (unresolvedConversationCount > 0) {
    rows.push({
      icon: CommentDiscussionIcon,
      iconColor: 'var(--fgColor-attention)',
      label: `${unresolvedConversationCount} unresolved conversation${unresolvedConversationCount === 1 ? '' : 's'}`,
    })
  }

  const deploymentBlocked = BlockedSection.failingConditionsAndRules.some(condition =>
    condition.displayName.toLowerCase().includes('deployment'),
  )

  if (deploymentBlocked) {
    rows.push({
      icon: RocketIcon,
      iconColor: 'var(--fgColor-attention)',
      label: 'Deployment required',
    })
  }

  return rows
}
